import React, { useCallback, useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Text } from '@common-fe/common-fe';

import { Modal } from '@/components';
import { IClaimDtoClaimStatusEnum } from '@/dtos';
import useSubmitLettersOfMedicalNecessityPopup from '@/modules/MyDocuments/hooks/useSubmitLettersOfMedicalNecessityPopup';
import { useLettersOfMedicalNecessityPopupStore } from '@/modules/MyDocuments/stores/useLettersOfMedicalNecessityPopup.store';
import theme from '@/styles/theme';

import ClaimsReviewList from './ClaimsReviewList';

interface Props {
  onSetVisible: (value: boolean) => void;
  onSuccess?: (value: string) => void;
  onFileUploadError?: (error?: FileError) => void;
  isMobile?: boolean;
}

const ClaimsReviewPopup: React.FC<Props> = ({
  onSetVisible,
  onSuccess,
  onFileUploadError,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { handleSuccessfullyCreated } = useSubmitLettersOfMedicalNecessityPopup({ onFileUploadError });
  const { sourceState } = useLettersOfMedicalNecessityPopupStore();

  const stillRequiringAttentionClaims = useMemo(
    () =>
      sourceState?.associatedClaims?.filter(
        (claim) => claim?.statusType === IClaimDtoClaimStatusEnum.NEEDS_HELP
      ),
    [sourceState?.associatedClaims]
  );

  const associatedClaims = useMemo(
    () =>
      sourceState?.associatedClaims?.filter(
        (claim) => claim?.statusType !== IClaimDtoClaimStatusEnum.NEEDS_HELP
      ),
    [sourceState?.associatedClaims]
  );

  const handleClose = useCallback(() => {
    onSuccess?.(sourceState?.createdLMNId || '');
    handleSuccessfullyCreated();
    onSetVisible(false);
  }, [onSetVisible, handleSuccessfullyCreated, onSuccess, sourceState]);

  return (
    <Modal
      testId="lmn-claims_review"
      visible
      onSetVisible={handleClose}
      {...(isMobile
        ? {
            contentStyle: {
              paddingLeft: theme.spacings.spacing8,
              paddingRight: theme.spacings.spacing8,
            },
          }
        : {})}
    >
      <Text data-testId="lmn-claims_review_title" size="2xl" textAlign="center" weight={700}>
        {t('Claims')}
      </Text>

      <Box
        data-testId="lmn-claims_review_wrapper"
        margin={{ top: 'spacing24' }}
        pad={isMobile ? 'spacing12' : 'spacing24'}
        background="module"
        round="moduleRound"
        gap="spacing32"
      >
        {associatedClaims?.length ? (
          <ClaimsReviewList
            testId="claims-sent-for-review_list-wrapper"
            title={t('Claims sent for review')}
            claims={associatedClaims}
            isMobile={isMobile}
          />
        ) : null}

        {stillRequiringAttentionClaims?.length ? (
          <ClaimsReviewList
            testId="still-requiring-attention-claims_list-wrapper"
            title={t('Claims still requiring proof of payment or additional documentation')}
            claims={stillRequiringAttentionClaims}
            isStillRequiringAttention
            isMobile={isMobile}
          />
        ) : null}
      </Box>

      <Box margin={{ top: 'spacing24' }} alignSelf="end">
        <AppButton testId="lmn-claims_review_close" width="160px" buttonType="primary" onClick={handleClose}>
          {t('Close')}
        </AppButton>
      </Box>
    </Modal>
  );
};

export default ClaimsReviewPopup;
